import { i18n, i18nExists } from '@shared/i18n';
import DateTimeRangeField from '@shared/fields/date-time-range-field';
import IdField from '@shared/fields/id-field';
import DateTimeField from '@shared/fields/date-time-field';
import { GenericModel } from '@shared/model/generic-model';
import StringField from '@shared/fields/string-field';
import DateModelField from '@shared/fields/date-model-field';
import BooleanField from '@shared/fields/boolean-field';
import EmailField from '@shared/fields/email-field';
import PhoneField from '@shared/fields/phone-field';
import AddressField from '@shared/fields/address-field';
import ImagesField from '@shared/fields/images-field';
import IntegerField from '@shared/fields/integer-field';
import JsonField from '@shared/fields/json-field';

function label(name, countryCode) {
  const defaultLabelCode = `entities.stripePerson.fields.${name}`;
  if (!countryCode && i18nExists(defaultLabelCode) && typeof i18n(defaultLabelCode) === 'string') return i18n(defaultLabelCode);
  
  const labelByCountryCode = `entities.stripePerson.fields.${name}.${countryCode}`;
  const defaultLabelByCountryCode = `entities.stripePerson.fields.${name}.default`;

  if (i18nExists(labelByCountryCode)) return i18n(labelByCountryCode);
  if (i18nExists(defaultLabelByCountryCode)) return i18n(defaultLabelByCountryCode);
  return '';
}

const fields = ({ countryCode, maxOwnershipPercentage }) => ({
  id: new IdField('id', label('id')),
  stripePersonId: new IdField('stripePersonId', label('stripePersonId')),
  created: new DateTimeField('created', label('created')),
  name: new StringField('name', label('name')),
  firstName: new StringField('firstName', label('firstName'), {
    required: true,
  }),
  lastName: new StringField('lastName', label('lastName'), {
    required: true,
  }),
  phone: new PhoneField('phone', label('phone'), {
    required: true,
  }),
  birthdate: new DateModelField('birthdate', label('birthdate'), {
    required: true,
    onlyPastDate: true,
    minAge: 13,
  }),
  jobTitle: new StringField('jobTitle', label('jobTitle'), {
    required: true,
  }),
  email: new EmailField('email', label('email'), {
    required: true,
  }),
  role: new StringField('role', label('role'), {
  }),
  isAccountOpener: new BooleanField('isAccountOpener', label('isAccountOpener')),
  isExecutive: new BooleanField('isExecutive', label('isExecutive')),
  isOwner: new BooleanField('isOwner', label('isOwner')),
  isDirector: new BooleanField('isDirector', label('isDirector')),
  ownershipPercentage: new IntegerField('ownershipPercentage', label('ownershipPercentage'), {
    min: 0,
    max: maxOwnershipPercentage,
    forceErrorI18nKey: 'entities.stripePerson.validation.maxOwnershipPercentage',
    required: true,
  }),
  address: new AddressField('address', label('address')),
  ssnLast4: new StringField('ssnLast4', label('ssnLast4', countryCode), {
    required: true,
    min: 4,
    max: 4,
  }),
  ssnLast4Provided: new BooleanField('ssnLast4Provided', label('ssnLast4Provided', countryCode)),
  verificationIdFront: new ImagesField('verificationIdFront', label('verificationIdFront'), null, {
    max: 1,
    required: true,
  }),
  verificationIdBack: new ImagesField('verificationIdBack', label('verificationIdBack'), null, {
    max: 1,
    required: true,
  }),
  additionalDocumentFront: new ImagesField('additionalDocumentFront', label('additionalDocumentFront'), null, {
    max: 1,
    required: true,
  }),
  additionalDocumentBack: new ImagesField('additionalDocumentBack', label('additionalDocumentBack'), null, {
    max: 1,
    required: true,
  }),

  verification: new JsonField('verification', label('verification')),
  
  createdAt: new DateTimeField(
    'createdAt',
    label('createdAt'),
  ),
  updatedAt: new DateTimeField(
      'updatedAt',
      label('updatedAt'),
  ),
  createdAtRange: new DateTimeRangeField(
      'createdAtRange',
      label('createdAtRange'),
  ),
});

export class StripePersonModel extends GenericModel {
  static fields({ countryCode = 'US', maxOwnershipPercentage = 100, isAccountOpener = false } = {}) {
    return fields({ countryCode, maxOwnershipPercentage, isAccountOpener });
  }

  static presenter(row, fieldName, countryCode) {
    const fields = this.fields({ countryCode });

    if (!fields[fieldName]) {
      throw new Error(`${fieldName} not found`);
    }

    return fields[fieldName].forPresenter(
      row[fields[fieldName].name],
    );
  }
}
